import type { ModerationComment } from '../../../shared/api/comment-moderation.api';

/**
 * Вердикт модели — словами, которые можно проверить глазами.
 *
 * Сервер пишет причину для журнала и отладки: «Jev insult 0.73», «Jev
 * gov_criticism 0.57 — под порогом», «Jev максимум 0.12». Человеку у кнопки
 * необратимого удаления это не говорит ничего: непонятно ни насколько модель
 * уверена, ни что делать с «максимум 0.12».
 *
 * Числа не прячем — они нужны, когда споришь с моделью, — но выносим их за
 * словесную оценку, а не вместо неё.
 */

export type ReasonTone = 'sure' | 'likely' | 'edge' | 'silent' | 'none';

export type HumanReason = {
  text: string;
  tone: ReasonTone;
};

/** Насколько модель уверена — тремя ступенями, а не голой цифрой. */
function confidence(p: number): { word: string; tone: ReasonTone } {
  if (p >= 0.85) return { word: 'уверенно', tone: 'sure' };
  if (p >= 0.65) return { word: 'скорее да', tone: 'likely' };
  return { word: 'на грани', tone: 'edge' };
}

export function humanReason(
  comment: Pick<ModerationComment, 'verdict'>,
  categories: Record<string, string> = {},
): HumanReason {
  const { reason, category } = comment.verdict;
  const name = categories[category] ?? category;

  // Модель промолчала — это не «чисто». Разница важная: такой комментарий никто
  // не смотрел, и прогон с двумя сотнями молчаний выглядел бы как чистый.
  if (reason === 'модель не ответила') {
    return { text: 'Модель не ответила — посмотрите сами', tone: 'silent' };
  }
  if (reason === 'без текста') {
    return { text: 'Только эмодзи и знаки — разбирать нечего', tone: 'none' };
  }

  const underThreshold = reason.includes('под порогом');
  const match = reason.match(/(\d+[.,]\d+)/);
  const p = match ? Number.parseFloat(match[1].replace(',', '.')) : null;

  if (reason.includes('максимум')) {
    return {
      text: p === null ? 'Ничего не сработало' : `Ничего не сработало (максимум ${p.toFixed(2)})`,
      tone: 'none',
    };
  }

  if (p === null) return { text: reason, tone: 'edge' };

  if (underThreshold) {
    return { text: `Похоже на «${name}», но не уверена (${p.toFixed(2)})`, tone: 'edge' };
  }

  const { word, tone } = confidence(p);
  return { text: `${name} — ${word} (${p.toFixed(2)})`, tone };
}
